"use client"

import { memo, useEffect, useId, useRef } from "react"
import { useRouter } from "../lib/router"
import type { Locale } from "../lib/locales"
import styles from "./hero-flow.module.css"

const trackSpacing = 140
const joinSpacing = 100
const firstTrackX = 80
const firstJoinY = 200

const alternatives = [
  {
    title: { ar: "الأكثر جودة", en: "Best quality" },
  },
  {
    title: { ar: "الأكثر أمانًا", en: "Most secure" },
  },
  {
    title: { ar: "الأوفر", en: "Economical" },
  },
  {
    title: { ar: "الأسرع", en: "Fastest" },
  },
] as const

const copy = {
  title: { ar: "مسار أوركست", en: "Orkest Path" },
  description: {
    ar: "ترتيب المسارات من البداية إلى النهاية: الأكثر جودة، الأكثر أمانًا، الأوفر، الأسرع، ثم مسار أوركست. الأسرع هو الأقرب لأوركست ويلتقي به أولًا من الأعلى، ثم الأوفر، ثم الأكثر أمانًا، وأخيرًا الأكثر جودة. تبدأ كل نبضة من أحد الفروع وتستمر داخل مسار أوركست حتى نقطة الوصول دون انقطاع.",
    en: "The paths in reading order are: best quality, most secure, economical, fastest, then Orkest. Fastest is closest to Orkest and joins first at the top, followed by economical, most secure, and finally best quality. Every packet starts on a branch and continues seamlessly along Orkest to the destination.",
  },
  salesTitle: { ar: "تحوّل رقمي يدعم نمو أعمالك", en: "Digital transformation for your business growth" },
  salesBody: {
    ar: "أنظمة مترابطة، عمل متكرر أقل، وقرارات أوضح.",
    en: "Connected systems, fewer repetitive tasks, and clearer decisions.",
  },
  keywordsLabel: { ar: "مجالات التحول الرقمي", en: "Digital transformation capabilities" },
} as const

const salesKeywords = [
  { ar: "التحول الرقمي", en: "Digital transformation" },
  { ar: "أتمتة الأعمال", en: "Business automation" },
  { ar: "تكامل الأنظمة", en: "System integration" },
  { ar: "قابلية التوسع", en: "Scalability" },
] as const

// Fastest sits closest to Orkest and merges first; Best quality is farthest and merges last.
function createScene(locale: Locale) {
  const rtl = locale === "ar"
  const lastTrackX = firstTrackX + alternatives.length * trackSpacing
  const orkestX = rtl ? 720 - lastTrackX : lastTrackX
  const tracks = alternatives.map((track, index) => {
    const ltrX = firstTrackX + index * trackSpacing
    const x = rtl ? 720 - ltrX : ltrX
    const joinY = firstJoinY + (alternatives.length - 1 - index) * joinSpacing
    const direction = x < orkestX ? 1 : -1
    const entry = `M${x} 88V${joinY - 32}Q${x} ${joinY} ${x + direction * 32} ${joinY}`
    return {
      ...track,
      x,
      path: `${entry}H${orkestX}`,
      // The same packet rounds the junction, then travels down the blue track.
      flowPath: `${entry}H${orkestX - direction * 12}Q${orkestX} ${joinY} ${orkestX} ${joinY + 12}V580`,
    }
  })
  return {
    orkestX,
    tracks,
    backgroundPath: tracks.map((track) => track.path).join(" "),
  }
}

// Geometry is computed once per language; animation needs no JavaScript frame updates.
const scenes = { ar: createScene("ar"), en: createScene("en") }

const HeroArtwork = memo(function HeroArtwork({ locale }: { locale: Locale }) {
  const id = useId()
  const { orkestX, tracks, backgroundPath } = scenes[locale]
  const gradientId = `${id}-flow-gradient`
  const gradientStops = locale === "ar"
    ? [["0%", "var(--orkest-white)"], ["13%", "var(--orkest-white)"], ["15%", "var(--color-blue)"], ["100%", "var(--color-blue)"]]
    : [["0%", "var(--color-blue)"], ["85%", "var(--color-blue)"], ["87%", "var(--orkest-white)"], ["100%", "var(--orkest-white)"]]
  const t = (value: { ar: string; en: string }) => value[locale]

  return (
    <svg
      className={styles.art}
      viewBox="0 0 720 640"
      role="img"
      aria-labelledby={`${id}-title ${id}-description`}
      lang={locale}
      direction={locale === "ar" ? "rtl" : "ltr"}
    >
      <title id={`${id}-title`}>{t(copy.title)}</title>
      <desc id={`${id}-description`}>{t(copy.description)}</desc>
      <defs>
        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="720" y2="0">
          {gradientStops.map(([offset, color]) => <stop key={offset} offset={offset} stopColor={color} />)}
        </linearGradient>
      </defs>
      <g aria-hidden="true">
        <path d={backgroundPath} className={styles.branch} strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
        {tracks.map(({ x, title }, index) => (
          <text key={index} x={x} y="48" textAnchor="middle" className={styles.heading}>{t(title)}</text>
        ))}
        {tracks.map(({ x }, index) => (
          <circle key={index} cx={x} cy="88" r="8" className={styles.origin} strokeWidth="2" />
        ))}
        <image href="/brand/orkest-blue.svg" x={orkestX - 68} y="30" width="136" height="20" />
        <line x1={orkestX} y1="88" x2={orkestX} y2="580" className={styles.coreGlow} strokeWidth="30" strokeLinecap="round" />
        <line x1={orkestX} y1="88" x2={orkestX} y2="580" className={styles.core} strokeWidth="22" strokeLinecap="round" />
        {tracks.map(({ flowPath }, index) => (
          <path key={index} data-branch={index} d={flowPath} pathLength="560" stroke={`url(#${gradientId})`} className={styles.flow} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
        ))}
        <circle cx={orkestX} cy="88" r="10" className={styles.featuredOrigin} />
        <circle cx={orkestX} cy="88" r="4" className={styles.originCenter} />
        <g transform={`translate(${orkestX} 580)`}>
          <circle r="30" className={styles.arrivalPulse} />
          <circle r="30" className={styles.destinationRing} strokeWidth="2" />
          <circle r="22" className={styles.destinationSurface} />
          <circle r="15" className={styles.destination} />
          <path d="m-8 0 5 5 11-12" className={styles.check} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>
    </svg>
  )
})

export function HeroFlow() {
  const { route } = useRouter()
  const locale = route.locale
  const figureRef = useRef<HTMLElement>(null)
  const t = (value: { ar: string; en: string }) => value[locale]

  useEffect(() => {
    const figure = figureRef.current
    if (!figure) return
    const device = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } }
    if ((device.hardwareConcurrency ?? 4) <= 2 || (device.deviceMemory ?? 4) <= 1 || device.connection?.saveData) {
      figure.dataset.perf = "eco"
    }
    let inView = !("IntersectionObserver" in window)
    const syncPlayback = () => {
      figure.dataset.flowPaused = String(!inView || document.hidden)
    }
    const observer = "IntersectionObserver" in window ? new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting
      syncPlayback()
    }) : null
    observer?.observe(figure)
    document.addEventListener("visibilitychange", syncPlayback)
    syncPlayback()
    return () => {
      observer?.disconnect()
      document.removeEventListener("visibilitychange", syncPlayback)
    }
  }, [])

  return (
    <figure ref={figureRef} className={styles.figure} lang={locale} dir={locale === "ar" ? "rtl" : "ltr"}>
      <HeroArtwork locale={locale} />
      <figcaption className={styles.sales}>
        <p className={styles.salesTitle}>{t(copy.salesTitle)}</p>
        <p className={styles.salesBody}>{t(copy.salesBody)}</p>
        <ul className={styles.keywords} aria-label={t(copy.keywordsLabel)}>
          {salesKeywords.map((keyword) => <li key={keyword.en}>{t(keyword)}</li>)}
        </ul>
      </figcaption>
    </figure>
  )
}
